import type { ChangeEvent, UIEvent } from 'react';
import React, { useState } from 'react';

import { usePatchUser } from '@vlabs/api/artemy/client';
import type { UserRecord } from '@vlabs/api/artemy/model';
import { CheckIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import { toast } from 'react-toastify';

import { useAttachDepartmentUsersTranslation } from '../i18n';
import type { AttachDepartmentUsersProps } from '../types';
import { useAttachDepartmentUsersOptions } from './useAttachDepartmentUsersOptions';

const POPUP_WIDTH = 280;
const POPUP_MAX_HEIGHT = 360;

interface AttachDepartmentUsersDropdownProps extends AttachDepartmentUsersProps {
  children: React.ReactElement;
  disabled?: boolean;
}

function AttachDepartmentUsersDropdown({
  children,
  departmentId,
  disabled = false,
}: AttachDepartmentUsersDropdownProps): React.JSX.Element {
  const { t } = useAttachDepartmentUsersTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const {
    fetchNextPage,
    hasError,
    hasMore,
    isFetchingNextPage,
    isLoading,
    users,
  } = useAttachDepartmentUsersOptions({ isOpen, query });

  const patchUser = usePatchUser({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        setIsOpen(false);
      },
      onError: () => {
        toast.error(t('on_error'));
      },
    },
  });

  const handleAttachUser = (user: UserRecord): void => {
    const departmentIds = user.department_ids;

    patchUser.mutate({
      userId: user.user_id,
      data: {
        department_ids: departmentIds.includes(departmentId)
          ? departmentIds
          : [...departmentIds, departmentId],
      },
    });
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);

    if (!open) {
      setQuery('');
    }
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  };

  const canShowUsers = !isLoading && !hasError;
  const canShowEmptyState = canShowUsers && users.length === 0 && !hasMore;
  const isDisabled = disabled || patchUser.isPending;

  let item: React.ReactNode = null;

  if (isLoading) {
    item = <DropdownMenu.EmptyItem>{t('loading')}</DropdownMenu.EmptyItem>;
  } else if (hasError) {
    item = <DropdownMenu.EmptyItem>{t('load_error')}</DropdownMenu.EmptyItem>;
  } else if (canShowEmptyState) {
    item = <DropdownMenu.EmptyItem>{t('empty')}</DropdownMenu.EmptyItem>;
  } else if (canShowUsers) {
    item = users.map((user) => {
      const isAttached = user.department_ids?.includes(departmentId);

      return (
        <DropdownMenu.Item
          decorationRight={isAttached ? <CheckIcon /> : undefined}
          description={user.full_name}
          disabled={patchUser.isPending || isAttached}
          key={user.user_id}
          onClick={() => handleAttachUser(user)}
        >
          {user.email}
        </DropdownMenu.Item>
      );
    });
  }

  // дозагружаем следующую страницу только после ручного скролла внутри дропдауна
  const handlePopupScroll = (e: UIEvent<HTMLDivElement>): void => {
    const { clientHeight, scrollHeight, scrollTop } = e.currentTarget;
    // если остаток меньше 8px, считаем, что пользователь дошел до низа:
    const isScrolledToBottom = scrollHeight - scrollTop - clientHeight < 8;

    if (canShowUsers && hasMore && !isFetchingNextPage && isScrolledToBottom) {
      fetchNextPage();
    }
  };

  return (
    <DropdownMenu.Root
      modal={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <DropdownMenu.Trigger
        disabled={isDisabled}
        render={children}
      />
      <DropdownMenu.Portal>
        <DropdownMenu.Positioner>
          <DropdownMenu.Popup
            onScroll={handlePopupScroll}
            style={{
              maxHeight: POPUP_MAX_HEIGHT,
              overflowY: 'auto',
              width: POPUP_WIDTH,
            }}
          >
            <DropdownMenu.SearchItem
              onChange={handleQueryChange}
              placeholder={t('search_placeholder')}
              size="small"
              value={query}
            />
            {item}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export { AttachDepartmentUsersDropdown };
